import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, InlineMessage, SaveBar, SectionCard, StatusChip } from '../ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/api';
import { tokens } from '../../theme/tokens';
import { MAX_PICKS_PER_KIND, type RelatedPickItem } from '../../types/relatedProducts';
import type { PickListKey, StoreRelatedPicksState } from './useStoreRelatedPicks';

const T = 'relatedProducts';

const imageOf = (product: { images?: unknown }): string | null => {
  const images = Array.isArray(product.images) ? product.images : [];
  const first = images.find((i: any) => (typeof i === 'string' && i) || i?.url) as any;
  if (!first) return null;
  return typeof first === 'string' ? first : String(first.url);
};

/** A product the picker offers, from the store's own catalog search (`/store/products-for-sync`). */
export const toPickItem = (product: { id: string; name?: string; model?: string; brand?: string; images?: unknown }): RelatedPickItem => ({
  id: product.id,
  name: product.name || product.model || product.id,
  brand: product.brand || '',
  image: imageOf(product),
  available: true,
});

interface StoreRelatedProductsEditorProps {
  state: StoreRelatedPicksState;
  onOpenProduct: (item: RelatedPickItem) => void;
}

/**
 * "Related products" on the store product details (flag
 * `retailer_related_products`, MOT-705): the store's own related and upsell
 * picks for this product, on top of the works-with suggestions. The picker
 * searches the store's own catalog; order is set with the arrows; edits are
 * saved with the dirty-only save bar. A member who is not an owner or admin
 * sees the lists read-only.
 */
export function StoreRelatedProductsEditor({ state, onOpenProduct }: StoreRelatedProductsEditorProps) {
  const { t } = useAppTranslation();
  const { showToast } = useToast();

  const save = async () => {
    try {
      await state.save();
      showToast(t(`${T}.saved`), 'success');
    } catch (error) {
      showToast((error as Error).message || t(`${T}.saveFailed`), 'error');
    }
  };

  return (
    <SectionCard dense padded title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)}>
      {state.loading ? (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : state.error ? (
        <InlineMessage severity="error">{t(`${T}.loadError`)}</InlineMessage>
      ) : (
        <Stack spacing={2.5}>
          {!state.canEdit && (
            <Typography variant="caption" color="text.secondary">{t(`${T}.readOnly`)}</Typography>
          )}
          {(['related', 'upsell'] as const).map((list) => (
            <PickList key={list} list={list} state={state} onOpenProduct={onOpenProduct} />
          ))}
        </Stack>
      )}
      <SaveBar
        dirty={state.dirty && state.canEdit}
        message={t(`${T}.saveMessage`)}
        saving={state.saving}
        onSave={() => void save()}
        onDiscard={state.discard}
      />
    </SectionCard>
  );
}

function PickList({ list, state, onOpenProduct }: { list: PickListKey; state: StoreRelatedPicksState; onOpenProduct: (item: RelatedPickItem) => void }) {
  const { t } = useAppTranslation();
  const items = state.draft[list];
  const full = items.length >= MAX_PICKS_PER_KIND;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">{t(`${T}.${list}Label`)}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {t(`${T}.${list}Hint`)}
      </Typography>
      {items.length === 0 ? (
        <EmptyState size="compact" title={t(`${T}.empty`)} />
      ) : (
        <Stack spacing={0.75} component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {items.map((item, index) => (
            <PickRow
              key={item.id}
              item={item}
              index={index}
              count={items.length}
              canEdit={state.canEdit}
              onOpen={() => onOpenProduct(item)}
              onMove={(delta) => state.move(list, index, delta)}
              onRemove={() => state.remove(list, item.id)}
            />
          ))}
        </Stack>
      )}
      {state.canEdit && (
        <Box sx={{ mt: 1 }}>
          {full ? (
            <Typography variant="caption" color="text.secondary">{t(`${T}.limit`, { count: MAX_PICKS_PER_KIND })}</Typography>
          ) : (
            <ProductPicker
              label={t(`${T}.addTo`, { list: t(`${T}.${list}Label`) })}
              exclude={new Set([state.productId, ...state.draft.related.map((i) => i.id), ...state.draft.upsell.map((i) => i.id)])}
              onPick={(item) => state.add(list, item)}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function PickRow(props: {
  item: RelatedPickItem;
  index: number;
  count: number;
  canEdit: boolean;
  onOpen: () => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useAppTranslation();
  const { item, index, count, canEdit } = props;
  const name = item.available ? item.name : t(`${T}.unavailable`);
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.75,
        borderRadius: 1,
        border: `1px solid ${tokens.line.default}`,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ width: 18, textAlign: 'right', flexShrink: 0 }}>
        {index + 1}
      </Typography>
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: tokens.surface.sunken,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.image ? <Box component="img" src={item.image} alt="" loading="lazy" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : null}
      </Box>
      <Box
        sx={{ minWidth: 0, flex: 1, cursor: item.available ? 'pointer' : 'default' }}
        onClick={item.available ? props.onOpen : undefined}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={name}>{name}</Typography>
        {item.available ? (
          <Typography variant="caption" color="text.secondary" noWrap component="div">{item.brand}</Typography>
        ) : (
          <StatusChip label={t(`${T}.unavailableChip`)} tone="caution" />
        )}
      </Box>
      {canEdit && (
        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Tooltip title={t(`${T}.moveUp`, { name })}>
            <span>
              <IconButton size="small" aria-label={t(`${T}.moveUp`, { name })} disabled={index === 0} onClick={() => props.onMove(-1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t(`${T}.moveDown`, { name })}>
            <span>
              <IconButton size="small" aria-label={t(`${T}.moveDown`, { name })} disabled={index === count - 1} onClick={() => props.onMove(1)}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t(`${T}.remove`, { name })}>
            <IconButton size="small" aria-label={t(`${T}.remove`, { name })} onClick={props.onRemove}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
}

/** Search the store's own catalog (its brands and its own uploads, as the product browser lists them). */
function ProductPicker({ label, exclude, onPick }: { label: string; exclude: Set<string>; onPick: (item: RelatedPickItem) => void }) {
  const { t } = useAppTranslation();
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(handle);
  }, [input]);
  const results = useQuery({
    queryKey: ['related-picker-search', search],
    queryFn: ({ signal }) => apiService.getStoreProducts(10, 0, { search, signal }),
    enabled: search.length >= 2,
    staleTime: 30_000,
  });
  const options = (results.data?.data ?? []).map((p) => toPickItem(p as any)).filter((o) => !exclude.has(o.id));

  return (
    <Autocomplete<RelatedPickItem>
      size="small"
      options={options}
      value={null}
      inputValue={input}
      onInputChange={(_e, value, reason) => {
        if (reason !== 'reset') setInput(value);
      }}
      onChange={(_e, value) => {
        if (value) {
          onPick(value);
          setInput('');
        }
      }}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={results.isFetching}
      noOptionsText={search.length >= 2 ? t(`${T}.noResults`) : t(`${T}.typeToSearch`)}
      renderOption={(optionProps, option) => {
        const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <li key={key} {...rest}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">{option.brand}</Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} label={label} placeholder={t(`${T}.addPlaceholder`)} />}
    />
  );
}
